import { Dispatch, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from '../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../UIPackage/ModalModule/components/ModalHeader';
import { IPassportGroup } from '../../APIPackage/PassportGroupsModule/interfaces/IPassportGroup';
import { ModalBody } from '../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../UIPackage/MainModule/components/Button';
import { formatDate } from '../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from '../../CorePackage/MainModule/constants/dateFormats';
import { RootActions } from '../../CorePackage/MainModule/types/RootActions';
import { passportGroupInitDetailFormAction } from '../MainModule/actions/passportGroupActions';
import { passportGroupInitFormSelector } from '../MainModule/selectors/passportGroupSelector';
import { PassportGroupDetailCondition } from './PassportGroupDetailCondition';
import styles from './PassportGroupDetailModal.module.scss';
import { FieldBlock } from '../../UIPackage/FormModule/components/FieldBlock';
import { SelectInput } from '../../UIPackage/MainModule/components/SelectInput';
import { UploadFile } from '../../UIPackage/MainModule/components/UploadFile';
import { useKnmDocumentAttachment } from '../../KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmAttachment } from '../../APIPackage/KnmModule/interfaces/IKnmAttachment';
import { PassportGroupTypeEnum } from '../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';

interface IPassportGroupDetailModalProps {
  group: IPassportGroup | undefined;
  onClose: () => void;
}

export const PassportGroupDetailModal = ({ group, onClose }: IPassportGroupDetailModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const values = useSelector(passportGroupInitFormSelector);
  const createdByAndCreatedAt = useMemo(() => {
    let result = '';

    if (group?.createdBy.fullName) {
      result += `${group.createdBy.fullName} `;
    }

    if (group?.createdBy.username) {
      result += `(${group.createdBy.username}) `;
    }

    if (group?.createdAt) {
      result += formatDate({
        date: group.createdAt,
        dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        parseFormat: DATE_FORMAT_ISO_FNS,
      });
    }

    return result;
  }, [group]);

  useEffect(() => {
    dispatch(passportGroupInitDetailFormAction());
  }, [dispatch]);

  const { downloadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const downloadAttachment = (attachment: IKnmAttachment) => () => {
    const { fileName, fileSize } = attachment;
    const filePath = attachment.filePath || null;
    const mimeType = attachment.mimeType || null;

    downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
  };

  const modalTitle = useMemo(() => {
    if (!group) {
      return '';
    }

    return `Группа мероприятий, подлежащих ${
      group.type === PassportGroupTypeEnum.CANCELLATION ? 'отмене' : 'удалению'
    } №${group.id}`;
  }, [group]);

  return (
    <Modal visible onClose={onClose}>
      <ModalHeader title={modalTitle} />
      <ModalBody overflow="scroll">
        <span className={styles.ActionInfo}>{`Создано ${createdByAndCreatedAt}`}</span>
        {values?.prosecutorOrganization && group?.type === PassportGroupTypeEnum.CANCELLATION && (
          <FieldBlock label="Наименование прокуратуры" margin="bottom">
            <SelectInput value={values.prosecutorOrganization} disabled />
          </FieldBlock>
        )}
        {values?.reason && (
          <FieldBlock label="Основание массовой отмены" margin="bottom">
            <SelectInput value={values.reason} disabled />
          </FieldBlock>
        )}
        {values?.reasonFile && (
          <FieldBlock label="Файл основания массовой отмены" color="black" margin="bottom">
            <UploadFile
              fileName={values.reasonFile.fileName || ''}
              fileSize={values.reasonFile.fileSize}
              onFileNameClick={downloadAttachment(values.reasonFile)}
            />
          </FieldBlock>
        )}
        {values?.includes?.map((item, index) => (
          <PassportGroupDetailCondition key={index} condition={item} />
        ))}
        {values?.exceptions?.map((item, index) => (
          <PassportGroupDetailCondition key={index} condition={item} />
        ))}
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" type="button" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
